// hooks/useFieldGeneration.js
import { useState } from 'react';
import axios from 'axios'; // eslint-disable-line no-unused-vars
import { API_KEYS, AI_PROVIDERS, getProviderForModel } from '../constants/aiConstants';
import { aiService } from '../services/aiService';

// Strip markdown bullet asterisks: "* • text" or "* text" → "• text" / "text"
const cleanBulletAsterisks = (text) =>
  text
    .split('\n')
    .map(line => line.replace(/^\*\s+/, ''))
    .join('\n');

export const useFieldGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateFieldContent = async (field, poi, data, model) => {
    try {
      setIsGenerating(field);
      
      // CRITICAL FIX: Always use the custom prompt when provided
      // Check if data.prompt exists and is not empty
      if (!data.prompt) {
        console.error(`ERROR: No prompt provided for field ${field}`);
      }
      
      // Use custom prompt if provided, NEVER create a default one
      const finalPrompt = data.prompt || '';
        
      console.log(`FINAL PROMPT BEING SENT TO API (field: ${field}): ${finalPrompt.substring(0, 150)}...`);
      console.log(`Using model: ${model}`);
      console.log(`Using language: ${data.language || 'en'}`);
      
      // Determine which API to use based on the model provider
      const provider = getProviderForModel(model);
      
      if (provider === AI_PROVIDERS.ANTHROPIC) {
        console.log('Using Anthropic API with model:', model);

        try {
          const response = await aiService.getAnthropicCompletion(
            [
              {
                role: 'system',
                content: "CRITICAL: Return ONLY the requested content. NO introductions, NO preambles, NO phrases like 'Here is...' or 'Sure...'. Start directly with the answer. If asked for bullet points, return ONLY bullet points. NO extra text before or after."
              },
              {
                role: 'user',
                content: finalPrompt
              }
            ],
            model,
            API_KEYS[AI_PROVIDERS.ANTHROPIC],
            {
              maxTokens: 1024,
              temperature: 0.7
            }
          );
          
          return cleanBulletAsterisks(response.trim());
        } catch (anthropicError) {
          console.error('Anthropic API error:', anthropicError);
          
          // Fall back to OpenAI if Anthropic fails
          console.log('Falling back to OpenAI due to Anthropic error');
          
          // Use OpenAI as fallback
          const fallbackResponse = await aiService.getOpenAICompletion(
            [
              {
                role: 'system',
                content: "CRITICAL: Return ONLY the requested content. NO introductions, NO preambles, NO phrases like 'Here is...' or 'Sure...'. Start directly with the answer. If asked for bullet points, return ONLY bullet points. NO extra text before or after."
              },
              {
                role: 'user',
                content: finalPrompt
              }
            ],
            'gpt-4o-mini',
            API_KEYS[AI_PROVIDERS.OPENAI],
            {
              maxTokens: 750,
              temperature: 0.7
            }
          );
          
          return cleanBulletAsterisks(fallbackResponse.trim());
        }
      } else if (provider === AI_PROVIDERS.OPENAI) {
        // Use OpenAI API for GPT models
        console.log('Using OpenAI API with model:', model);

        const response = await aiService.getOpenAICompletion(
          [
            {
              role: 'system',
              content: "CRITICAL: Return ONLY the requested content. NO introductions, NO preambles, NO phrases like 'Here is...' or 'Sure...'. Start directly with the answer. If asked for bullet points, return ONLY bullet points. NO extra text before or after."
            },
            {
              role: 'user',
              content: finalPrompt
            }
          ],
          model,
          API_KEYS[AI_PROVIDERS.OPENAI],
          {
            maxTokens: 750,
            temperature: 0.7
          }
        );
        
        return cleanBulletAsterisks(response.trim());
      } else if (provider === AI_PROVIDERS.GOOGLE) {
        // Implementation for Google API
        console.log('Using Google API with model:', model);

        const response = await aiService.getGoogleCompletion(
          [
            {
              role: 'system',
              content: "CRITICAL: Return ONLY the requested content. NO introductions, NO preambles, NO phrases like 'Here is...' or 'Sure...'. Start directly with the answer. If asked for bullet points, return ONLY bullet points. NO extra text before or after."
            },
            {
              role: 'user',
              content: finalPrompt
            }
          ],
          model,
          API_KEYS[AI_PROVIDERS.GOOGLE],
          {
            maxTokens: 2048,
            temperature: 0.7
          }
        );
        
        return cleanBulletAsterisks(response.trim());
      } else {
        throw new Error(`Unknown provider: ${provider}`);
      }
    } catch (error) {
      console.error('Error generating field content:', error);
      
      // Even more detailed error logging
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        if (error.response.data) {
          console.error('Response data:', error.response.data);
        }
      } else if (error.request) {
        console.error('No response received. Request details:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      
      throw new Error(`Failed to generate content: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return { generateFieldContent, isGenerating };
};

export default useFieldGeneration;